<template>
  <SmDrawer
    v-model:visibility="visible"
    :title="deploymentName || i18n.t('browserExtension.deployment')"
    @close="handleClose">
    <template #close>
      <div class="flex items-center gap-3">
        <SmButton size="small" outline @click="openManageDialog">
          {{ i18n.t('browserExtension.manage') }}
        </SmButton>
        <SmButtonClose @close="visible = false" />
      </div>
    </template>

    <div v-if="loading" class="flex flex-col gap-6">
      <!-- KPI Skeletons -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
        <el-card shadow="never">
          <SmSkeleton height="80px" width="100%" />
        </el-card>
      </div>

      <!-- Chart Skeleton -->
      <div
        class="flex-shrink-0 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <SmSkeleton height="20px" width="30%" class="mb-4" />
        <SmSkeleton height="250px" width="100%" />
      </div>
    </div>

    <div v-else class="flex flex-col gap-6">
      <!-- Error Alert -->
      <div
        v-if="error"
        class="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg p-4">
        <div class="flex items-center gap-2">
          <Activity class="h-4 w-4" />
          <p>{{ error }}</p>
        </div>
      </div>

      <!-- ID Subtitle -->
      <div class="-mt-4 mb-2">
        <p class="font-mono text-xs text-gray-400">{{ deploymentId }}</p>
      </div>

      <!-- Usage Summary -->
      <UsageSummary :stats="stats" variant="deployment" />

      <!-- Tabs -->
      <el-tabs v-model="activeTab" class="w-full">
        <!-- Top Tools Tab -->
        <el-tab-pane :label="i18n.t('browserExtension.topTools')" name="tools">
          <div class="mt-4 flex h-[500px] flex-col gap-4">
            <SmInput
              v-model="toolsSearch"
              outline
              size="medium"
              :label="i18n.t('browserExtension.searchToolId')"
              class="max-w-xs" />
            <ToolsTable
              :data="filteredToolsList"
              :loading="loadingTools"
              @row-click="handleToolClick" />
          </div>
        </el-tab-pane>

        <!-- Users Tab -->
        <el-tab-pane :label="i18n.t('users')" name="users">
          <div class="mt-4 flex h-[500px] flex-col gap-4">
            <SmInput
              v-model="usersSearch"
              outline
              size="medium"
              :label="searchUsersLabel"
              class="max-w-xs" />
            <UsersTable
              :data="filteredUsersList"
              :loading="loadingUsers"
              compact
              :visible-columns="['id', 'last_seen', 'total_sessions']"
              @row-click="handleUserClick" />
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- Manage Dialog -->
    <DeploymentManageDialog v-model:visibility="dialogManageVisible" />
  </SmDrawer>

  <!-- Person Detail Drawer -->
  <PersonDetailDrawer
    v-model:visibility="personDrawerVisible"
    :installation-id="selectedInstallationId"
    :person-email="selectedPersonEmail" />

  <!-- Tool Detail Drawer -->
  <ToolDetailDrawer
    v-model="toolDrawerVisible"
    :software-name="selectedSoftwareName"
    :initial-deployment-id="deploymentId" />
</template>

<script setup lang="ts">
  import { computed, ref, watch, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Activity } from 'lucide-vue-next'
  import SmDrawer from '@/components/sm/SmDrawer.vue'
  import SmButtonClose from '@/components/sm/SmButtonClose.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import ToolsTable from '../dashboard/tables/ToolsTable.vue'
  import UsersTable from '../dashboard/tables/UsersTable.vue'
  import DeploymentManageDialog from '../DeploymentManageDialog.vue'
  import PersonDetailDrawer from './PersonDetailDrawer.vue'
  import ToolDetailDrawer from './ToolDetailDrawer.vue'
  import UsageSummary from '../dashboard/widgets/UsageSummary.vue'
  import SmSkeleton from '@/components/sm/SmSkeleton.vue'
  import type {
    UsageStats,
    UsageStatsDetails,
    UsageStatsWithEmail,
  } from '@/client'
  import {
    getDeploymentUsageStats,
    getToolsStatsByDeployment,
    getAllInstallationsStats,
  } from '@/stores/browserextension/browserextension.store'
  import { useSessionStore } from '@/stores/sessionStore'

  const props = defineProps<{
    modelValue: boolean
    deploymentId: string | null
    deploymentName?: string
  }>()

  const emit = defineEmits(['update:modelValue'])

  const i18n = useI18n()
  const sessionStore = useSessionStore()
  const loading = ref(false)
  const loadingTools = ref(false)
  const loadingUsers = ref(false)
  const error = ref<string | null>(null)

  const stats = ref<UsageStatsDetails | null>(null)
  const toolsList = ref<UsageStats[]>([])
  const usersList = ref<UsageStatsWithEmail[]>([])
  const activeTab = ref('tools')
  const dialogManageVisible = ref(false)
  const toolsSearch = ref('')
  const usersSearch = ref('')

  // Sub-drawer state
  const personDrawerVisible = ref(false)
  const selectedInstallationId = ref<string | null>(null)
  const selectedPersonEmail = ref<string | null>(null)
  const toolDrawerVisible = ref(false)
  const selectedSoftwareName = ref<string | null>(null)

  // Request version tracking to prevent race conditions
  // Incremented on each new request, checked before applying results
  let requestVersion = 0

  const visible = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  })

  const browserExtensionEmailEnabled = computed(function () {
    return !!sessionStore.currentCompanyInfo?.features.browser_extension_email
  })

  const searchUsersLabel = computed(function () {
    if (browserExtensionEmailEnabled.value) {
      return i18n.t('browserExtension.searchUserIdOrEmail')
    }

    return i18n.t('browserExtension.searchUserId')
  })

  const filteredToolsList = computed(() => {
    if (!toolsSearch.value) {
      return toolsList.value
    }
    const searchLower = toolsSearch.value.toLowerCase()
    return toolsList.value.filter((item) =>
      item.id.toLowerCase().includes(searchLower)
    )
  })

  const filteredUsersList = computed(() => {
    if (!usersSearch.value) {
      return usersList.value
    }
    const searchLower = usersSearch.value.toLowerCase()
    return usersList.value.filter((item) => {
      const idMatches = item.id.toLowerCase().includes(searchLower)
      const emailMatches =
        browserExtensionEmailEnabled.value &&
        !!item.email &&
        item.email.toLowerCase().includes(searchLower)

      return idMatches || emailMatches
    })
  })

  // Invalidate pending requests on unmount
  onUnmounted(() => {
    requestVersion++
  })

  // Watch for opening or ID change to load main stats
  watch(
    () => [props.modelValue, props.deploymentId],
    async ([isVisible, newId]) => {
      if (isVisible && newId) {
        // Invalidate any pending requests from previous drawer session
        requestVersion++
        const currentVersion = requestVersion

        await loadStats(newId as string, currentVersion)

        // Check if request is still valid before continuing
        if (currentVersion !== requestVersion) return

        // Reset search and tab data
        toolsSearch.value = ''
        usersSearch.value = ''
        toolsList.value = []
        usersList.value = []
        // Load default tab data
        if (activeTab.value === 'tools')
          loadTools(newId as string, currentVersion)
        if (activeTab.value === 'users')
          loadUsers(newId as string, currentVersion)
      }
    }
  )

  // Watch for tab changes to lazy load
  watch(activeTab, (newTab) => {
    if (props.deploymentId) {
      if (newTab === 'tools' && toolsList.value.length === 0) {
        loadTools(props.deploymentId, requestVersion)
      }
      if (newTab === 'users' && usersList.value.length === 0) {
        loadUsers(props.deploymentId, requestVersion)
      }
    }
  })

  function handleClose() {
    // Invalidate pending requests when closing
    requestVersion++
    visible.value = false
  }

  function openManageDialog() {
    dialogManageVisible.value = true
  }

  async function loadStats(id: string, version: number) {
    try {
      loading.value = true
      const result = await getDeploymentUsageStats(id)

      // Only apply result if this request is still current
      if (version === requestVersion) {
        stats.value = result
      }
    } catch (e) {
      // Only show error if this request is still current
      if (version === requestVersion) {
        console.error('Failed to load deployment stats', e)
        error.value = i18n.t('errorLoadingData')
      }
    } finally {
      if (version === requestVersion) {
        loading.value = false
      }
    }
  }

  async function loadTools(id: string, version: number) {
    try {
      loadingTools.value = true
      const results = await getToolsStatsByDeployment(id)

      // Only apply result if this request is still current
      if (version === requestVersion) {
        toolsList.value = results
      }
    } catch (e) {
      if (version === requestVersion) {
        console.error('Failed to load tools', e)
      }
    } finally {
      if (version === requestVersion) {
        loadingTools.value = false
      }
    }
  }

  async function loadUsers(id: string, version: number) {
    try {
      loadingUsers.value = true
      const result = await getAllInstallationsStats(null, null, id)

      // Only apply result if this request is still current
      if (version === requestVersion) {
        usersList.value = result
      }
    } catch (e) {
      if (version === requestVersion) {
        console.error('Failed to load users', e)
      }
    } finally {
      if (version === requestVersion) {
        loadingUsers.value = false
      }
    }
  }

  function handleToolClick(_index: number, row: UsageStats) {
    selectedSoftwareName.value = row.id
    toolDrawerVisible.value = true
  }

  function handleUserClick(_index: number, row: UsageStatsWithEmail) {
    selectedPersonEmail.value = row.email || null
    selectedInstallationId.value = row.id
    personDrawerVisible.value = true
  }
</script>
